import { useState } from "react";
import { useFetcher } from "react-router-dom";
import { ReactComponentLike } from "prop-types";
import {
  RiBankCard2Line,
  RiDeleteBin6Line,
  RiPaypalLine,
  RiPencilLine
} from "@remixicon/react";

import Button from "components/Button";
import Card, { ICardProps } from "components/Card";

import Checkbox from "components/Checkbox";
import Spinner from "components/Spinner";
import { PureTooltip } from "components/Tooltip";

import {
  getPaymentSourceTitle,
  PaymentSource
} from "types/billing/PaymentSource";

import { BillingDetailsCardTitle } from "./BillingDetailsCard";

import { cx } from "utils";

import { PAYMENT_SOURCES_FETCHER_HREF, usePaymentSources } from "v2/pages/billing/hooks/usePaymentSources";
import { PaymentSourceActions, PaymentSourceInputName } from "v2/dataroutes/BillingData";
import { BillingFlowType, useBillingFlow } from "v2/pages/billing/providers/BillingFlowProvider";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import useBillingInfo from "v2/hooks/billing/useBillingInfo";
import { isLikelyChargeable } from "./paymentSourceChargeable";
import {
  trackPaymentMethodSetPrimarySubmitted,
  trackPaymentMethodRemoveSubmitted,
} from "utils/Tracking";

import PaymentSourceForm from "./PaymentSourceForm";
import RemovePrimaryPaymentSourceModal from "./RemovePrimaryPaymentSourceModal";
import {
  canEditPaymentSource,
  canMutatePaymentSources
} from "v2/utils/billingAvailability";


type ViewMode = "view" | "edit";

export default function PaymentSourceCard(
  {
    paymentSource,
    mode = "view",
    viewModeExtra,
    className
  }: {
    paymentSource: PaymentSource,
    mode?: ViewMode,
    viewModeExtra?: ICardProps["extra"],
    className?: string
  }
) {
  const [ showEditForm, setShowEditForm ] = useState(false);
  const { paymentSourcesFetcher, isSettingPrimary } = usePaymentSources();
  const { hasWireTransferOnAccount } = useBillingInfo();
  const { activeSubscription } = useActiveSubscription();
  const canMutate = canMutatePaymentSources(activeSubscription);

  const handleSetPrimary = () => {
    if (paymentSource.isPrimary || isSettingPrimary || !canMutate) return;
    trackPaymentMethodSetPrimarySubmitted(paymentSource.type);
    paymentSourcesFetcher.submit(
      {
        [PaymentSourceInputName.id]: paymentSource.id,
        [PaymentSourceInputName.action]: PaymentSourceActions.set_primary.value,
      },
      { method: "POST", action: PAYMENT_SOURCES_FETCHER_HREF }
    );
  };

  const darkerBackground = mode === "edit" && paymentSource.isPrimary;
  return (
    <Card
      className={ cx(darkerBackground && "bg-neutral-50", className) }
      title={
        <PaymentSourceTitle
          Icon={ paymentSource.type === "card" ? RiBankCard2Line : RiPaypalLine }
          title={ getPaymentSourceTitle(paymentSource) }
          subtitle={ paymentSource.type === "card" ? ("Expires " + String(paymentSource.expiry.month).padStart(2, "0") + "/" + paymentSource.expiry.year) : undefined }
          isPrimary={ mode === "edit" ? paymentSource.isPrimary : undefined }
          primaryLabel={ hasWireTransferOnAccount ? "PRIMARY BACKUP" : undefined }
          onSelectPrimary={ mode === "edit" ? handleSetPrimary : undefined }
          disabled={ isSettingPrimary || !canMutate }
        />
      }
      size="MD"
        extra={ mode === "view" ? viewModeExtra : <EditModeButtons paymentSource={ paymentSource } setShowEditForm={ setShowEditForm } /> }
    >
      { showEditForm && (
        <PaymentSourceForm
          paymentSource={ paymentSource }
          onSuccess={ () => setShowEditForm(false) }
          secondaryButton={ () =>
            <Button
              text="Cancel"
              className="button button-secondary"
              onClick={ () => setShowEditForm(false) }
            />
          }
        />
      ) }
    </Card>
  );
};

function PaymentSourceTitle(
  {
    title,
    subtitle,
    Icon,
    isPrimary,
    primaryLabel,
    onSelectPrimary,
    disabled
  }: {
    title: string,
    subtitle: string | undefined,
    Icon: ReactComponentLike,
    isPrimary?: boolean,
    primaryLabel?: string,
    onSelectPrimary?: () => void,
    disabled?: boolean
  }
) {
  return (
    <div className="flex flex-row gap-x-4 items-center">
      { isPrimary !== undefined && (
        <Checkbox
          pure
          checked={ isPrimary }
          disabled={ disabled }
          onChange={ onSelectPrimary }
        />
      ) }
      <div className="bg-neutral-50 flex items-center justify-center size-12 rounded">
        <Icon className="text-neutral-500 size-6" />
      </div>
      <BillingDetailsCardTitle title={ title } subtitle={ subtitle } isPrimary={ isPrimary } primaryLabel={ primaryLabel }/>
    </div>
  );
}

// Removing a primary card schedules cancellation only when no way to pay remains: PLG, VBAN status known, no wire transfer, no backup.
function primaryRemovalNeedsCancellation(
  paymentSource: PaymentSource,
  flowType: BillingFlowType,
  isBillingInfoLoaded: boolean,
  hasWireTransferOnAccount: boolean,
  promotableBackup: PaymentSource | undefined,
  hasScheduledCancellation: boolean,
): boolean {
  return paymentSource.isPrimary
    && flowType === "plg"
    && isBillingInfoLoaded
    && !hasWireTransferOnAccount
    && !hasScheduledCancellation
    && !promotableBackup;
}

function EditModeButtons(
  {
    paymentSource,
    setShowEditForm
  }: {
    paymentSource: PaymentSource,
    setShowEditForm: (show: boolean) => void
  }
) {
  const { paymentSources, paymentSourcesFetcher } = usePaymentSources();
  const { flowType } = useBillingFlow();
  const { activeSubscription } = useActiveSubscription();
  const { hasWireTransferOnAccount, isBillingInfoLoaded, isBillingInfoLoading } = useBillingInfo();
  const paypalFetcher = useFetcher({ key: "add-paypal-payment-source" });
  const [ showRemovePrimaryModal, setShowRemovePrimaryModal ] = useState(false);

  const isPaymentSourceUpdating = paymentSourcesFetcher.state === "submitting" || paypalFetcher.state !== "idle";
  const canMutate = canMutatePaymentSources(activeSubscription);
  const isSubmitting = paymentSourcesFetcher.state === "submitting" &&
    paymentSourcesFetcher.formData?.get(PaymentSourceInputName.id) === paymentSource.id;

  const hasScheduledCancellation = activeSubscription?.scheduledSubscription?.planSlug === "free";
  const promotableBackup = (paymentSource.isPrimary && flowType !== "free" && !hasScheduledCancellation)
    ? paymentSources?.find(ps => ps.id !== paymentSource.id && isLikelyChargeable(ps))
    : undefined;
  const requiresConfirmation = primaryRemovalNeedsCancellation(
    paymentSource, flowType, isBillingInfoLoaded, hasWireTransferOnAccount, promotableBackup, hasScheduledCancellation,
  );
  // Block direct removal only while VBAN status is still loading; an error releases it (falls through to direct remove).
  const awaitingVbanStatus =
    paymentSource.isPrimary && flowType === "plg" && !hasScheduledCancellation && !promotableBackup && isBillingInfoLoading;

  // Wire-transfer accounts must always keep at least one card/PayPal as a backup, so block
  // removing the last remaining source. Adding another re-enables removal.
  const isLastPaymentSource = paymentSources?.length === 1;
  const blockRemoval = hasWireTransferOnAccount && isLastPaymentSource;

  // Until billing-info settles we don't yet know if this is a wire-transfer account, so hold the
  // last card's removal for every flow type (an error releases it — backend stays authoritative).
  const awaitingBackupCheck = isLastPaymentSource && isBillingInfoLoading;

  return (
    <div className="flex flex-row gap-x-4">
      { paymentSource.type !== "paypal" && canEditPaymentSource(activeSubscription) && (
        <Button
          text="Edit"
          className={ cx("button button-tertiary", paymentSource.isPrimary && "hover:dark:!bg-neutral-100") }
          size="SM"
          icon={ {
            Icon: RiPencilLine,
            size: "size-3.5"  // <- to match the XXS size of the spinner
          } }
          disabled={ isPaymentSourceUpdating }
          onClick={ () => setShowEditForm(true) }
        />
      ) }
      { blockRemoval ? (
        <PureTooltip content="Please add a new backup payment method before removing this card.">
          <Button
            type="button"
            text="Remove"
            className={ cx("button button-tertiary", paymentSource.isPrimary && "hover:dark:!bg-neutral-100") }
            size="SM"
            icon={ {
              Icon: RiDeleteBin6Line,
              size: "size-3.5"
            } }
            disabled
          />
        </PureTooltip>
      ) : requiresConfirmation ? (
        <Button
          type="button"
          text="Remove"
          className={ cx("button button-tertiary", paymentSource.isPrimary && "hover:dark:!bg-neutral-100") }
          size="SM"
          icon={ {
            Icon: isSubmitting ? () => <Spinner size="XXS" theme="secondary" /> : RiDeleteBin6Line,
            size: "size-3.5"
          } }
          disabled={ isSubmitting || isPaymentSourceUpdating || !canMutate }
          onClick={ () => setShowRemovePrimaryModal(true) }
        />
      ) : (
        <paymentSourcesFetcher.Form
          method="POST"
          action={ PAYMENT_SOURCES_FETCHER_HREF }
        >
          <input type="hidden" name={ PaymentSourceInputName.id } value={ paymentSource.id } />
          { promotableBackup && (
            <input type="hidden" name={ PaymentSourceInputName.newPrimaryId } value={ promotableBackup.id } />
          ) }
          <Button
            type="submit"
            text="Remove"
            className={ cx("button button-tertiary", paymentSource.isPrimary && "hover:dark:!bg-neutral-100") }
            size="SM"
            icon={ {
              Icon: (isSubmitting || awaitingVbanStatus || awaitingBackupCheck) ? () => <Spinner size="XXS" theme="secondary" /> : RiDeleteBin6Line,
              size: "size-3.5"
            } }
            disabled={ isSubmitting || isPaymentSourceUpdating || awaitingVbanStatus || awaitingBackupCheck || !canMutate }
            onClick={ () => trackPaymentMethodRemoveSubmitted(paymentSource.type) }
            { ...(promotableBackup ? PaymentSourceActions.remove_with_promotion : PaymentSourceActions.remove) }
          />
        </paymentSourcesFetcher.Form>
      ) }
      { showRemovePrimaryModal && (
        <RemovePrimaryPaymentSourceModal
          paymentSource={ paymentSource }
          onClose={ () => {
            setShowRemovePrimaryModal(false);
            return true;  // prevent the modal to do its usual navigation on close
          } }
        />
      ) }
    </div>
  );
}
